import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";

import styles from "./index.module.scss";

import XtInputNumber, { XtInputNumberRefProps, XtInputNumberProps } from "components/xt/input/number";

interface AppInputNumberProps extends Omit<XtInputNumberProps, "prefix"> {
  classInput?: string;
  disabled?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  isErr?: boolean;
  noBtns?: boolean;
  noHandleSuffixEvent?: boolean;
}

const AppInputNumber: React.FC<AppInputNumberProps> = ({
  className,
  classInput,
  disabled = false,
  prefix,
  suffix,
  isErr,
  noBtns,
  noHandleSuffixEvent,
  ...rest
}) => {
  const refInput = useRef<XtInputNumberRefProps>(null);
  const [isFocus, setIsFocus] = useState(false);

  const onIncrease = useCallback(() => {
    if (refInput.current && refInput.current.increase) refInput.current.increase();
  }, []);
  const onDecrease = useCallback(() => {
    if (refInput.current && refInput.current.decrease) refInput.current.decrease();
  }, []);

  const clsStatus = useMemo(() => {
    if (isErr) return styles.err;
    if (isFocus) return styles.atv;
  }, [isErr, isFocus]);

  const interval = useRef<number>();
  const once = useCallback((event, fn) => {
    const listener = function () {
      fn && fn();
      document.removeEventListener(event, listener);
    };
    document.addEventListener(event, listener);
  }, []);
  const handleMouseDown = useCallback((fn) => {
    const startTime = Date.now();
    once("mouseup", () => {
      if (Date.now() - startTime < 100) {
        fn();
      }
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = 0;
      }
    });
    interval.current && clearInterval(interval.current);
    interval.current = window.setInterval(fn, 100);
  }, []);
  const handleKeyDown = useCallback((e, fn) => {
    e.stopPropagation();
    const keyName = e.key;
    if (keyName === "Enter" || keyName === " ") {
      fn();
      e.preventDefault();
    }
  }, []);
  useEffect(() => {
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = 0;
      }
    };
  }, []);

  const handleSuffixEvent = () => {
    if (noHandleSuffixEvent) return;
    refInput.current && refInput.current.focus();
  };

  return (
    <div className={cx(styles.main, clsStatus, className)} aria-disabled={disabled}>
      <div className={styles.content}>
        {!!prefix && <div className={styles.prefix}>{prefix}</div>}
        <XtInputNumber
          ref={refInput}
          className={cx(styles.input, classInput)}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          disabled={disabled}
          {...rest}
        />
        {!!suffix && (
          <div className={styles.suffix} onClick={handleSuffixEvent}>
            {suffix}
          </div>
        )}
      </div>
      {!noBtns && (
        <div className={styles.btns}>
          <button
            disabled={disabled}
            className={cx("btnTxt", styles.btnUp)}
            onKeyDown={(e) => handleKeyDown(e, onIncrease)}
            onMouseDown={() => handleMouseDown(onIncrease)}
          >
            <span></span>
          </button>
          <span></span>
          <button
            disabled={disabled}
            className={cx("btnTxt", styles.btnDown)}
            onKeyDown={(e) => handleKeyDown(e, onDecrease)}
            onMouseDown={() => handleMouseDown(onDecrease)}
          >
            <span></span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AppInputNumber;
